import { Combobox, useCombobox, InputBase } from '@mantine/core';
import { useState, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { RivalType } from 'api/api.types';

type RivalSelectProps = {
  rivals: RivalType[];
  selectedRival: RivalType | null;
  onRivalSelect: (rival: RivalType) => void;
};

const RivalSelect = memo(function RivalSelect({
  rivals,
  selectedRival,
  onRivalSelect,
}: RivalSelectProps) {
  const { t } = useTranslation(['Dashboard', 'Common']);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setValue(selectedRival?.name || ''); // Reset search to selected rival name
    },
    onDropdownOpen: () => {
      setValue(''); // Clear search when opening to show all options
    },
  });

  const [value, setValue] = useState(selectedRival?.name || '');

  const filteredOptions = useMemo(() => {
    // Don't filter if the value matches the selected rival exactly
    if (!value.trim() || value === selectedRival?.name) {
      return rivals;
    }

    return rivals.filter((rival) =>
      rival.name.toLowerCase().includes(value.toLowerCase()),
    );
  }, [rivals, value, selectedRival?.name]);

  const options = filteredOptions.map((rival) => (
    <Combobox.Option value={rival.id.toString()} key={rival.id}>
      {rival.name}
    </Combobox.Option>
  ));

  const handleOptionSubmit = (optionValue: string) => {
    const rival = rivals.find((r) => r.id.toString() === optionValue);
    if (rival) {
      setValue(rival.name);
      onRivalSelect(rival);
    }
    combobox.closeDropdown();
  };

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      withinPortal={true}
    >
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          rightSection={<Combobox.Chevron style={{ color: '#620dec' }} />}
          rightSectionPointerEvents="none"
          onClick={(e) => {
            e.stopPropagation();
            combobox.toggleDropdown();
          }}
          size="xs"
          variant="filled"
          rightSectionWidth={24}
        >
          {selectedRival?.name ||
            t('Dashboard:askKlue.promptLibrary.selectRival')}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown
        style={{ minWidth: '200px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Combobox.Search
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          placeholder={t('Dashboard:askKlue.promptLibrary.searchRivals')}
          onKeyDown={(e) => e.stopPropagation()}
          styles={{
            input: {
              width: '100%',
            },
          }}
        />
        <Combobox.Options mah={200} style={{ overflowY: 'auto' }}>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>
              {t('Dashboard:askKlue.promptLibrary.noRivalsFound')}
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
});

export default RivalSelect;
